import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
    Box,
    Button,
    Stack,
    Grid,
    Typography,
    TextField,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Upload as UploadIcon,
    School as SchoolIcon,
} from '@mui/icons-material';

import { Container, Input, ErrorMessage, InfoLabel, SectionHeader, StyledCard } from '@/components/common';
import { useCreateCourse } from '@/hooks/courses.hook';
import { useInstructorList } from '@/hooks/instructor.hook';
import { successToast, errorToast, choiceDialog } from '@/common/toast';
import { rollbackFailedCourseCreation } from '@/apis/courses';
import { LANGUAGE_OPTIONS } from '@/constants/courses';

/**
 * 과정 등록 페이지 컴포넌트
 * 
 * 새로운 교육 과정을 등록할 수 있는 페이지입니다.
 */
const CourseAddPage = () => {
    const navigate = useNavigate();
    const createCourseMutation = useCreateCourse();
    const { data: instructorList = [], isLoading: instructorLoading } = useInstructorList();

    // 파일 선택 상태
    const [thumbnailImg, setThumbnailImg] = useState(null);
    const [introImg, setIntroImg] = useState(null);
    const [lessonPlanFile, setLessonPlanFile] = useState(null);

    // 폼 관리
    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        watch,
    } = useForm({
        defaultValues: {
            courseName: '',
            courseCategory: 'SOFTWARE_SECURITY',
            courseCount: 1,
            courseObj: '',
            languageId: 0,
            freeYn: false,
            hasTraining: false,
            instId: '',
        },
    });

    const freeYn = watch('freeYn');

    /**
     * 파일 선택 핸들러
     */
    const handleFileSelect = useCallback((fieldName, event) => {
        const file = event.target.files?.[0];
        if (file) {
            if (fieldName === 'thumbnailImg') {
                setThumbnailImg(file);
            } else if (fieldName === 'introImg') {
                setIntroImg(file);
            } else if (fieldName === 'lessonPlanFile') {
                setLessonPlanFile(file);
            }
        }
    }, []);

    /**
     * 과정 등록 핸들러
     */
    const onSubmit = useCallback(
        async (data) => {
            // 파일 필수 검증
            if (!thumbnailImg) {
                errorToast('썸네일 이미지를 선택해주세요.');
                return;
            }
            if (!introImg) {
                errorToast('소개 이미지를 선택해주세요.');
                return;
            }
            // if (!lessonPlanFile) {
            //     errorToast('강의 교안 파일을 선택해주세요.');
            //     return;
            // }

            try {
                await createCourseMutation.mutateAsync({
                    courseName: data.courseName,
                    category: data.courseCategory,
                    courseCount: parseInt(data.courseCount) || 1,
                    courseObj: data.courseObj || '',
                    languageId: parseInt(data.languageId) || 0,
                    freeYn: data.freeYn || false,
                    hasTraining: data.hasTraining || false,
                    instId: data.instId ? parseInt(data.instId) : null,
                    thumbnailImg: thumbnailImg,
                    introImg: introImg,
                    lessonPlanFile: lessonPlanFile ? lessonPlanFile : null,
                });
                successToast('과정이 성공적으로 등록되었습니다.');
                navigate('/education/courses');
            } catch (error) {
                // 미디버스 카테고리 생성 실패인 경우 선택 다이얼로그 표시
                if (error.midibusCategoryFailed && error.createdCourseId) {
                    const sanitizedCourseName = data.courseName.replace(/\s/g, '');
                    choiceDialog(
                        `미디버스 카테고리 생성에 실패했습니다.\n과정명: "${data.courseName}" (카테고리명: "${sanitizedCourseName}")\n\n어떻게 하시겠습니까?`,
                        '과정 유지 (미디버스에서 직접 생성)',
                        '롤백하고 다시 시도',
                        async () => {
                            // 과정 유지 선택: 안내 메시지 표시
                            successToast('과정이 등록되었습니다. 미디버스에서 카테고리를 직접 생성해주세요.');
                            navigate('/education/courses');
                        },
                        async () => {
                            // 롤백 선택: 과정 삭제
                            try {
                                await rollbackFailedCourseCreation(error.createdCourseId);
                                errorToast('과정 등록이 취소되었습니다. 다시 시도해주세요.');
                            } catch (deleteError) {
                                errorToast('과정 삭제에 실패했습니다. 관리자에게 문의해주세요.');
                                console.error('과정 삭제 오류:', deleteError);
                            }
                        }
                    );
                } else {
                    errorToast(`과정 등록에 실패했습니다. - ${error.response?.data?.msg}`);
                }
            }
        },
        [createCourseMutation, navigate, thumbnailImg, introImg, lessonPlanFile]
    );

    /**
     * 뒤로가기 핸들러
     */
    const handleGoBack = useCallback(() => {
        navigate('/education/courses');
    }, [navigate]);

    return (
        <Container title='과정 등록'>
            <Stack spacing={3}>
                {/* 상단 버튼 영역 */}
                <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2}>
                    <Button
                        variant='outlined'
                        onClick={handleGoBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{ minWidth: 120 }}
                    >
                        목록으로
                    </Button>
                    <Button
                        variant='contained'
                        onClick={handleSubmit(onSubmit)}
                        startIcon={createCourseMutation.isPending ? <CircularProgress size={16} /> : <SaveIcon />}
                        disabled={createCourseMutation.isPending}
                        sx={{ minWidth: 100 }}
                    >
                        등록
                    </Button>
                </Box>

                {/* 안내 메시지 */}
                <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <SchoolIcon sx={{ color: '#1976d2' }} />
                        <Typography variant='body2' color='text.secondary'>
                            과정 정보를 입력하고 등록 버튼을 클릭하여 과정을 등록할 수 있습니다.
                        </Typography>
                    </Stack>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={3}>
                        {/* 왼쪽: 과정 기본 정보 */}
                        <Grid item xs={12} md={8}>
                            <StyledCard>
                                <CardContent sx={{ p: 3 }}>
                                    <SectionHeader>
                                        <SchoolIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                                        <Typography variant='h5' fontWeight={600}>
                                            과정 정보
                                        </Typography>
                                    </SectionHeader>

                                    <Grid container spacing={3}>
                                        {/* 과정명 */}
                                        <Grid item xs={12}>
                                            <InfoLabel>과정명 *</InfoLabel>
                                            <Input
                                                control={control}
                                                name='courseName'
                                                textFieldProps={{
                                                    fullWidth: true,
                                                    required: true,
                                                    variant: 'outlined',
                                                    placeholder: '과정명을 입력하세요',
                                                }}
                                                rules={{
                                                    required: '과정명을 입력해주세요.',
                                                }}
                                            />
                                            <ErrorMessage errors={errors} name='courseName' />
                                        </Grid>

                                        {/* 카테고리 */}
                                        <Grid item xs={12} sm={6}>
                                            <InfoLabel>카테고리 *</InfoLabel>
                                            <Controller
                                                name="courseCategory"
                                                control={control}
                                                rules={{ required: '카테고리를 선택해주세요.' }}
                                                render={({ field }) => (
                                                    <FormControl fullWidth error={!!errors.courseCategory}>
                                                        <Select {...field}>
                                                            <MenuItem value='SOFTWARE_SECURITY'>소프트웨어 개발보안 교육</MenuItem>
                                                            <MenuItem value='INFORMATION_SECURITY'>정보보안 교육</MenuItem>
                                                            <MenuItem value='PROGRAMMING'>프로그래밍 교육</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                            <ErrorMessage errors={errors} name='courseCategory' />
                                        </Grid>

                                        {/* 언어 */}
                                        <Grid item xs={12} sm={6}>
                                            <InfoLabel>언어 *</InfoLabel>
                                            <Controller
                                                name="languageId"
                                                control={control}
                                                rules={{ required: '언어를 선택해주세요.' }}
                                                render={({ field }) => (
                                                    <FormControl fullWidth error={!!errors.languageId}>
                                                        <Select {...field} value={field.value || 0}>
                                                            {LANGUAGE_OPTIONS.map((lang) => (
                                                                <MenuItem key={lang.languageId} value={lang.languageId}>
                                                                    {lang.languageName}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                            <ErrorMessage errors={errors} name='languageId' />
                                        </Grid>

                                        {/* 과정 수 */}
                                        <Grid item xs={12} sm={6}>
                                            <InfoLabel>과정 수 *</InfoLabel>
                                            <Input
                                                control={control}
                                                name='courseCount'
                                                textFieldProps={{
                                                    fullWidth: true,
                                                    type: 'number',
                                                    required: true,
                                                    variant: 'outlined',
                                                    placeholder: '1',
                                                }}
                                                rules={{
                                                    required: '과정 수를 입력해주세요.',
                                                    min: {
                                                        value: 1,
                                                        message: '과정 수는 1 이상이어야 합니다.',
                                                    },
                                                }}
                                            />
                                            <ErrorMessage errors={errors} name='courseCount' />
                                        </Grid>

                                        {/* 강사 */}
                                        <Grid item xs={12} sm={6}>
                                            <InfoLabel>강사 *</InfoLabel>
                                            <Controller
                                                name="instId"
                                                control={control}
                                                rules={{ required: '강사를 선택해주세요.' }}
                                                render={({ field }) => (
                                                    <FormControl fullWidth error={!!errors.instId}>
                                                        <Select {...field} value={field.value || ''} displayEmpty>
                                                            <MenuItem value='' disabled>강사를 선택하세요</MenuItem>
                                                            {instructorLoading ? (
                                                                <MenuItem disabled>로딩 중...</MenuItem>
                                                            ) : (
                                                                instructorList.map((instructor) => (
                                                                    <MenuItem key={instructor.instId} value={instructor.instId}>
                                                                        {instructor.instName || instructor.instId}
                                                                    </MenuItem>
                                                                ))
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                            <ErrorMessage errors={errors} name='instId' />
                                        </Grid>

                                        {/* 무료 여부 */}
                                        <Grid item xs={12} sm={6}>
                                            <InfoLabel>무료 여부</InfoLabel>
                                            <Controller
                                                name="freeYn"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={<Switch {...field} checked={field.value} />}
                                                        label={field.value ? '무료' : '유료'}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        {/* 훈련 보유 */}
                                        <Grid item xs={12} sm={6}>
                                            <InfoLabel>진단 및 대응훈련 연계 여부</InfoLabel>
                                            <Controller
                                                name="hasTraining"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={<Switch {...field} checked={field.value} />}
                                                        label={field.value ? '연계' : '연계 안함'}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        {/* 과정 목표 */}
                                        <Grid item xs={12}>
                                            <InfoLabel>과정 목표 *</InfoLabel>
                                            <Controller
                                                name="courseObj"
                                                control={control}
                                                rules={{ required: '과정 목표를 입력해주세요.' }}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                        required
                                                        variant="outlined"
                                                        placeholder="'본 강의는 사이버공격에 효과적인 예방과 대응을 위해 SW 개발보안 인식 제고, 전문역량 강화를 목적으로 학습합니다.'와 같은 형태로 작성"
                                                        error={!!errors.courseObj}
                                                    />
                                                )}
                                            />
                                            <ErrorMessage errors={errors} name='courseObj' />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </StyledCard>
                        </Grid>

                        {/* 오른쪽: 파일 업로드 */}
                        <Grid item xs={12} md={4}>
                            <StyledCard>
                                <CardContent sx={{ p: 3 }}>
                                    <SectionHeader>
                                        <UploadIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                                        <Typography variant='h5' fontWeight={600}>
                                            파일 업로드
                                        </Typography>
                                    </SectionHeader>

                                    <Stack spacing={3}>
                                        {/* 썸네일 이미지 */}
                                        <Box>
                                            <InfoLabel>썸네일 이미지 *</InfoLabel>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileSelect('thumbnailImg', e)}
                                                style={{ display: 'none' }}
                                                id="thumbnail-img-input"
                                                required
                                            />
                                            <label htmlFor="thumbnail-img-input">
                                                <Button
                                                    variant="outlined"
                                                    component="span"
                                                    fullWidth
                                                    startIcon={<UploadIcon />}
                                                    sx={{ mb: 1 }}
                                                    color={!thumbnailImg ? 'error' : 'primary'}
                                                >
                                                    {thumbnailImg ? thumbnailImg.name : '썸네일 이미지 선택'}
                                                </Button>
                                            </label>
                                            {thumbnailImg ? (
                                                <Alert severity="success" sx={{ mt: 1 }}>
                                                    선택된 파일: {thumbnailImg.name}
                                                </Alert>
                                            ) : (
                                                <Alert severity="error" sx={{ mt: 1 }}>
                                                    썸네일 이미지를 선택해주세요. <br/>
                                                    [10MB 이하 너비 367px, 높이 210px의 이미지]
                                                </Alert>
                                            )}
                                        </Box>

                                        {/* 소개 이미지 */}
                                        <Box>
                                            <InfoLabel>소개 이미지 *</InfoLabel>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileSelect('introImg', e)}
                                                style={{ display: 'none' }}
                                                id="intro-img-input"
                                                required
                                            />
                                            <label htmlFor="intro-img-input">
                                                <Button
                                                    variant="outlined"
                                                    component="span"
                                                    fullWidth
                                                    startIcon={<UploadIcon />}
                                                    sx={{ mb: 1 }}
                                                    color={!introImg ? 'error' : 'primary'}
                                                >
                                                    {introImg ? introImg.name : '소개 이미지 선택'}
                                                </Button>
                                            </label>
                                            {introImg ? (
                                                <Alert severity="success" sx={{ mt: 1 }}>
                                                    선택된 파일: {introImg.name}
                                                </Alert>
                                            ) : (
                                                <Alert severity="error" sx={{ mt: 1 }}>
                                                    소개 이미지를 선택해주세요.<br/>
                                                    [10MB 이하 너비 800px의 이미지]
                                                </Alert>
                                            )}
                                        </Box>

                                        {/* 강의 교안 파일 */}
                                        <Box>
                                            <InfoLabel>강의 교안 파일</InfoLabel>
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.ppt,.pptx,.md"
                                                onChange={(e) => handleFileSelect('lessonPlanFile', e)}
                                                style={{ display: 'none' }}
                                                id="lesson-plan-input"
                                            />
                                            <label htmlFor="lesson-plan-input">
                                                <Button
                                                    variant="outlined"
                                                    component="span"
                                                    fullWidth
                                                    startIcon={<UploadIcon />}
                                                    sx={{ mb: 1 }}
                                                    color={!lessonPlanFile ? 'warning' : 'primary'}
                                                >
                                                    {lessonPlanFile ? lessonPlanFile.name : '강의 교안 선택'}
                                                </Button>
                                            </label>
                                            {lessonPlanFile ? (
                                                <Alert severity="success" sx={{ mt: 1 }}>
                                                    선택된 파일: {lessonPlanFile.name}
                                                </Alert>
                                            ) : (
                                                <Alert severity="warning" sx={{ mt: 1 }}>
                                                    강의 교안 파일을 선택해주세요.<br/>
                                                    [50MB 이하의 .pdf, .doc, .docx, .ppt, .pptx, .md]
                                                </Alert>
                                            )}
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </StyledCard>
                        </Grid>
                    </Grid>
                </form>
            </Stack>
        </Container>
    );
};

export default CourseAddPage;
